import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import { useWeddingData } from "../hooks/useWeddingData";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import {
  RiSearchLine,
  RiTeamLine,
  RiUserHeartLine,
  RiCloseCircleLine,
  RiWhatsappLine,
} from "react-icons/ri";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./StatusGuestsListComponent.scss";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

/**
 * Props:
 * - statusFilter?: "Pending" | "Not Participating" | "Participation Confirmed"
 * - liquorOnly?: boolean
 * - liquorValue?: "Yes" | "No"
 * - titleOverride?: string
 * - backTo?: string
 * - pageSize?: number
 * - hideFamily?: boolean
 * - hideStatus?: boolean
 * - showMobileWhatsApp?: boolean
 */
const StatusGuestsListComponent = ({
  statusFilter,
  liquorOnly = false,
  liquorValue = "Yes",
  titleOverride,
  pageSize = 10,
  hideFamily = false,
  hideStatus = false,
  showMobileWhatsApp = false,
}) => {
  const { user } = useAuth();
  const { weddingData, loading: weddingLoading } = useWeddingData();

  const [activeIdx, setActiveIdx] = useState(0);
  const [guestRows, setGuestRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const brideName = weddingData?.bride_name || "Bride";
  const groomName = weddingData?.groom_name || "Groom";

  const LISTS = useMemo(
    () => [
      {
        id: GROOM_COLLECTION,
        tabLabel: `${groomName}'s List`,
        name: groomName,
      },
      {
        id: BRIDE_COLLECTION,
        tabLabel: `${brideName}'s List`,
        name: brideName,
      },
    ],
    [groomName, brideName]
  );

  const listId = LISTS[activeIdx]?.id || "";
  const listName = LISTS[activeIdx]?.name || "Guest";

  const computedBaseTitle = statusFilter
    ? `${listName}'s ${statusFilter} Guest List`
    : `${listName}'s Guest List`;

  const title = titleOverride || computedBaseTitle;

  const statusTone = useMemo(() => {
    if (statusFilter === "Participation Confirmed") {
      return {
        label: "Confirmed guests",
        icon: <RiUserHeartLine />,
      };
    }

    if (statusFilter === "Pending") {
      return {
        label: "Pending guests",
        icon: <RiTeamLine />,
      };
    }

    if (statusFilter === "Not Participating") {
      return {
        label: "Not participating",
        icon: <RiCloseCircleLine />,
      };
    }

    return {
      label: "All guests",
      icon: <RiTeamLine />,
    };
  }, [statusFilter]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 760px)");

    const handleScreenChange = (e) => {
      setIsMobile(e.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleScreenChange);
    } else {
      mediaQuery.addListener(handleScreenChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleScreenChange);
      } else {
        mediaQuery.removeListener(handleScreenChange);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);
        const slug = weddingData?.slug || "";

        if (!slug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", slug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    if (!weddingLoading) {
      loadTheme();
    }

    return () => {
      isMounted = false;
    };
  }, [weddingData, weddingLoading]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const shouldHideFamily = hideFamily || isMobile;
  const shouldHideStatus = hideStatus || isMobile;
  const shouldShowWhatsApp = showMobileWhatsApp && isMobile;

  const openWhatsApp = () => {
    if (!shouldShowWhatsApp) return;

    const message = encodeURIComponent("Hi! This is a kind reminder to RSVP.");
    const appUrl = `whatsapp://send?text=${message}`;
    const fallbackUrl = "https://wa.me/";

    window.location.href = appUrl;

    setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.location.href = fallbackUrl;
      }
    }, 1200);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchGuestList = async () => {
      if (!user || !listId) return;

      setLoading(true);

      try {
        const snapshot = await getDocs(collection(db, "users", user.uid, listId));

        const collected = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const familyName = data.familyName || "Unknown";
          const guests = Array.isArray(data.guests) ? data.guests : [];

          guests.forEach((guest) => {
            const gName = guest?.name || "";
            const gStatus = guest?.status || "";
            const gLiquor = guest?.takingLiquor || "No";

            const matchesStatus = !statusFilter || gStatus === statusFilter;
            const matchesLiquor = !liquorOnly || gLiquor === liquorValue;

            if (matchesStatus && matchesLiquor) {
              collected.push({
                name: gName || "Unnamed Guest",
                familyName,
                status: gStatus || "Unknown",
                takingLiquor: gLiquor,
              });
            }
          });
        });

        collected.sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) {
          setGuestRows(collected);
        }
      } catch (e) {
        console.error(`Error fetching ${listId}:`, e);
        if (!cancelled) {
          setGuestRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!weddingLoading) {
      fetchGuestList();
    }

    return () => {
      cancelled = true;
    };
  }, [user, listId, statusFilter, liquorOnly, liquorValue, weddingLoading]);

  useEffect(() => {
    setCurrentPage(0);
    setSearchTerm("");
  }, [listId, statusFilter, liquorOnly, liquorValue]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return guestRows;

    return guestRows.filter((guest) => {
      const nameMatch = (guest.name || "").toLowerCase().includes(term);
      const familyMatch = (guest.familyName || "").toLowerCase().includes(term);
      return nameMatch || familyMatch;
    });
  }, [guestRows, searchTerm]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filteredRows.length]);

  if (!user || weddingLoading || themeLoading || !siteTheme) return null;

  const total = filteredRows.length;
  const start = currentPage * pageSize;
  const end = Math.min(start + pageSize, total);
  const pageRows = filteredRows.slice(start, end);

  const isFirstPage = currentPage === 0;
  const isLastPage = end >= total;

  const handlePrev = () => {
    if (!isFirstPage) setCurrentPage((p) => p - 1);
  };

  const handleNext = () => {
    if (!isLastPage) setCurrentPage((p) => p + 1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "G";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  };

  const showLiquorBadge = liquorOnly;

  return (
    <section className="statusGuests" style={themeStyle} aria-label="Status guest list">
      <div className="statusGuests__hero">
        <div>
          <p className="statusGuests__eyebrow">Guest Status Directory</p>
          <h1 className="statusGuests__title">{title}</h1>
          <p className="statusGuests__subtitle">
            Browse and review guests by response status with a cleaner, premium
            overview.
          </p>
        </div>
      </div>

      <div className="statusGuests__tabs" role="tablist" aria-label="Guest source">
        {LISTS.map((tab, i) => (
          <ButtonComponent
            key={tab.id}
            as="button"
            className={`statusGuests__tabBtn ${i === activeIdx ? "is-active" : ""}`}
            aria-pressed={i === activeIdx}
            onClick={() => setActiveIdx(i)}
          >
            {tab.tabLabel}
          </ButtonComponent>
        ))}
      </div>

      <div className="statusGuests__panel">
        <div className="statusGuests__toolbar">
          <div aria-live="polite">
            <strong>{total}</strong>
            <span>{total === 1 ? " guest found" : " guests found"}</span>
          </div>

          <div className="statusGuests__searchWrap">
            <span className="statusGuests__searchIcon">
              <RiSearchLine />
            </span>
            <input
              type="text"
              className="statusGuests__searchInput"
              placeholder="Search by guest name or family name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search guests"
            />
          </div>
        </div>

        {showLiquorBadge && (
          <div className="statusGuests__filterNote">
            Showing only guests with liquor preference: <strong>{liquorValue}</strong>
          </div>
        )}

        {loading ? (
          <div className="statusGuests__empty">Loading...</div>
        ) : total === 0 ? (
          <div className="statusGuests__empty">
            {searchTerm
              ? "No guests matched your search."
              : statusFilter
              ? `No guest is ${statusFilter}.`
              : "No guests found."}
          </div>
        ) : (
          <>
            <div className="statusGuests__tableWrap">
              <table className="statusGuests__table">
                <thead>
                  <tr>
                    <th>Guest</th>
                    {!shouldHideFamily && <th>Family</th>}
                    {!shouldHideStatus && <th>Status</th>}
                  </tr>
                </thead>

                <tbody>
                  {pageRows.map((guest, idx) => (
                    <tr key={`${guest.familyName}-${guest.name}-${start + idx}`}>
                      <td data-label="Guest">
                        <div className="statusGuests__guestCell">
                          <span className="statusGuests__avatar">
                            {getInitials(guest.name)}
                          </span>

                          <div className="statusGuests__guestMeta">
                            <div className="statusGuests__guestTopRow">
                              <span className="statusGuests__guestName">{guest.name}</span>

                              {shouldShowWhatsApp && (
                                <button
                                  type="button"
                                  className="statusGuests__whatsappInlineBtn"
                                  onClick={openWhatsApp}
                                  aria-label={`Open WhatsApp for ${guest.name}`}
                                  title="Open WhatsApp"
                                >
                                  <RiWhatsappLine />
                                </button>
                              )}
                            </div>

                            <span className="statusGuests__guestHint">
                              Guest profile
                            </span>
                          </div>
                        </div>
                      </td>

                      {!shouldHideFamily && (
                        <td data-label="Family">
                          <span className="statusGuests__familyBadge">
                            {guest.familyName}
                          </span>
                        </td>
                      )}

                      {!shouldHideStatus && (
                        <td data-label="Status">
                          <span className="statusGuests__statusBadge">
                            {guest.status}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="statusGuests__footer">
              <div className="statusGuests__pageMeta">
                Showing <strong>{start + 1}</strong>-<strong>{end}</strong> of{" "}
                <strong>{total}</strong>
              </div>

              <div className="statusGuests__pagination">
                <ButtonComponent
                  className="statusGuests__pageBtn is-ghost"
                  onClick={handlePrev}
                  disabled={isFirstPage}
                >
                  Previous
                </ButtonComponent>

                <div>
                  Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong>
                </div>

                <ButtonComponent
                  className="statusGuests__pageBtn"
                  onClick={handleNext}
                  disabled={isLastPage}
                >
                  Next
                </ButtonComponent>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default StatusGuestsListComponent;